import { Accordion, AccordionHeader, AccordionItem, AccordionPanel } from '@/components/UI/Accordion';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers.tsx';
import { formatNumber } from '@/helpers.tsx';
import { _isNotEmpty, _notNil } from '@/littledash.ts';
import { type FC } from 'react';
import { type ConsumptionMetric, type CageConsumptionMetricApiId } from '@/model/Consumption.model.ts';
import ConsumptionActions from './ConsumptionActions';
import { intakeLabels } from '@/components/Workflow/Show/Views/Consumption/Consumption.utils.ts';
import { type EditConsumptionRequest } from '@/components/Workflow/Show/WorkflowConsumption.tsx';
import { type DeleteConsumptionRequest } from './ConsumptionActions.tsx';
import UserAvatar from '@/components/UI/UserAvatar/UserAvatar';

interface ConsumptionListProps {
  measurements: Array<ConsumptionMetric>;
  onExcludeClick: (consumptionMetricApiId: CageConsumptionMetricApiId) => void;
  onEditClick: (consumptionMetricApiId: CageConsumptionMetricApiId, request: EditConsumptionRequest) => void;
  onDeleteClick: (consumptionMetricApiId: CageConsumptionMetricApiId, request: DeleteConsumptionRequest) => void;
}

const ConsumptionList: FC<ConsumptionListProps> = ({ measurements, onExcludeClick, onEditClick, onDeleteClick }) => {
  const type = measurements?.[0]?.type === 'food' ? 'food' : 'fluid';
  const inLabel = intakeLabels[type].in;
  const outLabel = intakeLabels[type].out;

  return (
    <Accordion>
      {measurements.map((m, index) => {
        const userCreated = m?.created_by;
        const userLastAccessed = m?.updated_by;

        return (
          <AccordionItem key={m.api_id}>
            <AccordionHeader className="f6 flex flex-wrap justify-between items-center mh3">
              <span className="near-black">
                {m.consumed
                  ? `${formatNumber(m.consumed.toFixed(2) ?? 0)} g/day`
                  : `${formatNumber(m.consumption_in ?? 0)} g`}
              </span>
              <DateTimeRenderer value={m.recorded_at} />
            </AccordionHeader>
            <AccordionPanel>
              <div className="ph3 pb3 flex">
                {userCreated && <UserAvatar user={userCreated} tooltip={userCreated.name ?? userCreated.email} />}
                <div className="w-100 pl3">
                  {_notNil(userLastAccessed) && (
                    <p className="f7 lh-copy db pb3">
                      Updated <DateTimeRenderer value={userLastAccessed.datetime} /> by{' '}
                      {userLastAccessed.name || userLastAccessed.email}
                    </p>
                  )}
                  {_isNotEmpty(m) && (
                    <ul className="pa3 br3 br--top--left bg-near-white ma0">
                      {_notNil(m.consumption_out) && (
                        <li className="f6 lh-copy">
                          <span className="near-black">{outLabel}:</span> {m.consumption_out} g
                        </li>
                      )}
                      {_notNil(m.spillage) && (
                        <li className="f6 lh-copy">
                          <span className="near-black">Spillage:</span> {m.spillage} g
                        </li>
                      )}
                      {_notNil(m.consumption_in) && (
                        <li className="f6 lh-copy">
                          <span className="near-black">{inLabel}:</span> {m.consumption_in} g
                        </li>
                      )}
                      {(m.days_lapsed ?? 0) > 0 && (
                        <li className="f6 lh-copy">
                          <span className="near-black">Days Lapsed:</span> {m.days_lapsed}
                        </li>
                      )}
                      {_notNil(m.consumed) && (
                        <li className="f6 lh-copy">
                          <span className="near-black">{`Animal ${intakeLabels[type].label} Intake`}:</span>{' '}
                          {formatNumber((m.consumed / m.population).toFixed(2))} g/day
                        </li>
                      )}
                      {_notNil(m.population) && (
                        <li className="f6 lh-copy">
                          <span className="near-black">Population:</span> {m.population}
                        </li>
                      )}
                      {m.comment && (
                        <li className="f6 lh-copy">
                          <span className="near-black">Comments:</span> {m.comment}
                        </li>
                      )}
                    </ul>
                  )}
                  <div className="mt2">
                    <ConsumptionActions
                      onExcludeClick={() => onExcludeClick(m.api_id)}
                      onEditClick={onEditClick}
                      onDeleteClick={onDeleteClick}
                      currentMetric={m}
                      previousMetric={measurements?.[1]}
                      canEdit={index == 0}
                      canExclude={m.status !== 'initial'}
                    />
                  </div>
                </div>
              </div>
            </AccordionPanel>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
};

export default ConsumptionList;
